import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import groupBy from 'lodash/groupBy';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import RadioGroup from '@mui/material/RadioGroup';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import {Check, LinkPopover} from '@vedmarket/ui-kit';

import ModalBig from 'components/common/modalBig';
import {COLORS} from 'config/constants';
import {formatReferenceCopy as formatDemoCopy} from 'utils/typography';

import './modal_chooseVat.scss';

export const getMergeVatRates = (vatRate?: Vat[]) => groupBy(vatRate || [], 'MainCondition');

const ModalChooseVat = ({
  isOpen,
  onClose,
  insideModal,
  vatRate,
  handleSave,
  currentVatRate,
  hasUniqValues,
}: CustomsVatModalProps) => {
  const {t} = useTranslation();

  const [ selectedIndex, setSelectedIndex ] = useState('');

  const mergeVatRates = getMergeVatRates(vatRate);
  const hasVatRateChoice = (vatRate?.length || 0) > 1;
  const selectedRate = vatRate?.[Number(selectedIndex)];
  const hasSelection = selectedIndex !== '' && Boolean(selectedRate);
  const selectedNumericValue = selectedRate?.ValueDetail?.ValueCount;
  const canSaveSelection = hasUniqValues ? hasSelection && selectedNumericValue !== undefined : hasSelection;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const savedIndex = vatRate?.findIndex((rate) => {
      if (typeof currentVatRate === 'number') {
        return hasUniqValues && Number(rate.ValueDetail?.ValueCount) === currentVatRate;
      }

      if (!currentVatRate) {
        return false;
      }

      const sameCondition = rate.Condition === currentVatRate.Condition;
      const sameDirectory = rate.Directory?.RuName === currentVatRate.Directory?.RuName;
      const sameGroup = rate.MainCondition === currentVatRate.MainCondition;
      const sameValue = rate.Value === currentVatRate.Value;

      const sameDescription = sameCondition && sameDirectory;
      const sameRate = sameGroup && sameValue;

      return sameDescription && sameRate;
    });

    setSelectedIndex(savedIndex !== undefined && savedIndex >= 0 ? String(savedIndex) : '');
  }, [ isOpen, currentVatRate, vatRate, hasUniqValues ]);

  const handleSend = () => {
    if (!canSaveSelection || !selectedRate) {
      return;
    }

    const value = hasUniqValues ? selectedNumericValue : selectedRate;

    if (value === undefined) {
      return;
    }

    handleSave(value);
    onClose();
  };

  return (
    <ModalBig
      isOpen={isOpen}
      onClose={onClose}
      title={t('calc__text__customs__choosingTheVatRate')}
      insideModal={insideModal}
    >
      <div className='choose-vat'>
        <div className='choose-vat__header'>
          <Typography variant='h6'>
            {formatDemoCopy(t('actions__message__selectValue'))}
          </Typography>
          {hasVatRateChoice && <LinkPopover
            linkText={t('calc__help__vatRateLink')}
            text={formatDemoCopy(t('calc__help__vatRate'))}
          />}
        </div>
        <FormControl fullWidth>
          <RadioGroup
            name='vat-rate'
            value={selectedIndex}
            onChange={(event) => setSelectedIndex(event.target.value)}
          >
            {Object.entries(mergeVatRates).map(([ title, rates ]) => (
              <div className='choose-vat__group' key={title}>
                <Divider />
                {rates[0].Directory?.RuName && (
                  <Typography variant='caption' sx={{
                    marginTop: '16px',
                    display: 'block',
                  }}>
                    {rates[0].Directory.RuName}
                  </Typography>
                )}
                {title !== 'null' && (
                  <Typography variant='subtitle1' sx={{
                    fontWeight: 500,
                    marginBottom: '8px',
                  }}>
                    {title}
                  </Typography>
                )}
                {rates.map((rate) => {
                  const index = vatRate?.indexOf(rate);

                  return (
                    <FormControlLabel
                      key={index}
                      value={String(index)}
                      control={<Radio sx={{flexShrink: 0}} />}
                      sx={{
                        margin: 0,
                        alignItems: 'flex-start',
                      }}
                      label={
                        <Typography variant='body2' sx={{
                          color: COLORS.BLACK_DARK,
                          minWidth: 0,
                          paddingTop: '9px',
                        }}>
                          <Typography component='span' variant='inherit' sx={{fontWeight: 700}}>
                            {rate.Value}
                          </Typography>{' '}{rate.Condition}
                        </Typography>
                      }
                    />
                  );
                })}

                <Typography variant='body2' sx={{marginTop: '8px'}}>
                  <Link
                    href={rates[0].Link}
                    target='_blank'
                    rel='noopener noreferrer'
                    sx={{
                      display: 'block',
                      cursor: 'pointer',
                    }}
                  >
                    {rates[0].Document}
                  </Link>
                </Typography>
              </div>
            ))}

          </RadioGroup>
        </FormControl>
        <Divider />
        <div className='choose-vat__buttons'>
          <Button variant='outlined' fullWidth onClick={onClose}>
            <Typography variant='inherit'>{formatDemoCopy(t('actions__text__cancel'))}</Typography>
          </Button>
          <Button variant='contained' fullWidth disabled={!canSaveSelection} onClick={handleSend} startIcon={<Check />}>
            <Typography variant='inherit'>{formatDemoCopy(t('actions__text__save'))}</Typography>
          </Button>
        </div>
      </div>
    </ModalBig>
  );
};

export default ModalChooseVat;
